import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS } from "@/data/content";
import { useConfig } from "@/context/ConfigContext";
import { waLink } from "@/config/site";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const cfg = useConfig();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled || open
          ? "bg-cream/85 backdrop-blur-xl border-b border-darknavy/10 shadow-[0_8px_30px_rgba(11,31,59,0.08)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Logo light={!scrolled && !open} />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-display text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
                  scrolled || open
                    ? `hover:text-aqua ${isActive ? "text-aqua" : "text-darknavy/80"}`
                    : `hover:text-gold ${isActive ? "text-gold" : "text-cream/85"}`
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={waLink(cfg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="flex items-center gap-2 bg-navy px-5 py-2.5 font-display text-[13px] font-semibold uppercase tracking-[0.12em] text-cream transition-colors duration-300 hover:bg-aqua"
          >
            <WhatsAppIcon className="h-4 w-4" />
            WhatsApp
          </a>
        </nav>
        <button
          className={`flex h-11 w-11 items-center justify-center lg:hidden ${scrolled || open ? "text-darknavy" : "text-cream"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-darknavy/10 lg:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-5 py-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block py-3 font-display text-2xl font-medium text-darknavy hover:text-aqua transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <a
                href={waLink(cfg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-cta"
                className="mt-4 flex items-center justify-center gap-2 bg-navy px-5 py-4 font-display text-sm font-semibold uppercase tracking-[0.12em] text-cream"
              >
                <WhatsAppIcon className="h-4 w-4" />
                WhatsApp Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
